// Minimal Sentry wrapper - no dependencies, loads from CDN

declare global {
  interface Window {
    Sentry?: any;
    E4S_SENTRY_ROUTER?: any; // Global reference to router for tracing
  }
}

/**
 * Suppress errors from native mobile bridges (iOS WKWebView / Android WebView)
 * that inject JavaScript into the page but reference handlers that don't exist.
 * These are outside our control and create noisy Sentry issues.
 *
 * Registered early so it fires before Sentry's own global error handler.
 * E4S-V3-2R: window.webkit.messageHandlers.navigationPerformanceLoggerWithReply
 * E4S-V3-2F: iabjs://navigation_performance_logger_android (Java object gone)
 * E4S-V3-2B: postEvent method not found
 * E4S-V3-G:  InvalidAccessError from postMessage in Facebook in-app browser
 */
export function registerNativeBridgeErrorGuard(): void {
  if (typeof window === "undefined") return;

  const bridgePatterns = [
    "messageHandlers",
    "WKWebView was deallocated",
    "Java object is gone",
    "postEvent",
    "navigationPerformanceLogger",
    "navigationPerformanceLoggerWithReply",
    "setupIosCallbackHandler",
    // E4S-V3-G: Facebook in-app browser raises this when its injected
    // postMessage bridge is invoked on a context that no longer supports it.
    "InvalidAccessError",
  ];

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      const message = event.message ?? "";
      const filename = event.filename ?? "";
      const combined = `${message} ${filename}`;
      if (bridgePatterns.some((p) => combined.includes(p))) {
        event.preventDefault();
        return;
      }
    },
    true // capture phase — runs before Sentry's listener
  );
}

export async function initializeSentry() {
  console.log("[Sentry] Starting initialization...");

  // Only load in production

  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    console.log("[Sentry] Disabled for development (localhost/127.0.0.1)");
    return;
  }

  console.log("[Sentry] Production environment detected, loading Sentry...");

  try {
    // Load Sentry from CDN if not already loaded
    if (!window.Sentry) {
      console.log("[Sentry] Loading CDN script with tracing...");
      await loadSentryCDNWithTracing();
      console.log("[Sentry] CDN script with tracing loaded successfully");
    } else {
      console.log("[Sentry] CDN script already loaded");
    }

    if (window.Sentry) {
      // Detect region from hostname for multi-region deployments
      const region = detectRegion();
      const appVersion = getAppVersion();

      console.log(
        `[Sentry] Initializing with region: ${region}, version: ${appVersion}`
      );

      // Check if router is available for tracing
      const routerAvailable = typeof window.E4S_SENTRY_ROUTER !== "undefined";
      console.log(`[Sentry] Router available for tracing: ${routerAvailable}`);

      // Debug: Check what's available in Sentry
      console.log(
        "[Sentry] Available Sentry functions:",
        Object.keys(window.Sentry)
      );
      console.log(
        "[Sentry] BrowserTracing available:",
        typeof window.Sentry.BrowserTracing
      );
      console.log(
        "[Sentry] vueRouterInstrumentation available:",
        typeof window.Sentry.vueRouterInstrumentation
      );

      // Try to create BrowserTracing with error handling
      let browserTracingIntegration;
      try {
        if (typeof window.Sentry.BrowserTracing === "function") {
          console.log("[Sentry] Creating BrowserTracing integration...");

          // Create minimal BrowserTracing without router instrumentation first
          browserTracingIntegration = new window.Sentry.BrowserTracing({
            tracingOrigins: [
              "localhost",
              "entry4sports.com",
              "athleticsireland.ie",
              "dev25.entry4sports.co.uk",
              "eire.entry4sports.com",
              "entry.athleticsireland.ie",
              "uat.entry4sports.co.uk",
              "api.entry.athleticsireland.ie",
              /^\/api\//,
            ],
          });
          console.log("[Sentry] BrowserTracing created successfully");

          // Try to add router instrumentation if available
          if (routerAvailable && window.Sentry.vueRouterInstrumentation) {
            console.log("[Sentry] Adding Vue router instrumentation...");
            try {
              const routerInstrumentation =
                window.Sentry.vueRouterInstrumentation(
                  window.E4S_SENTRY_ROUTER
                );
              if (
                routerInstrumentation &&
                typeof routerInstrumentation === "function"
              ) {
                console.log("[Sentry] Router instrumentation added");
                // Note: We can't modify the BrowserTracing after creation, so this is just for debugging
              }
            } catch (routerError) {
              console.warn(
                "[Sentry] Router instrumentation failed, but continuing without it:",
                routerError
              );
            }
          }
        } else {
          console.warn(
            "[Sentry] BrowserTracing not available, using basic error tracking only"
          );
        }
      } catch (tracingError) {
        console.error(
          "[Sentry] Failed to create BrowserTracing:",
          tracingError
        );
        browserTracingIntegration = undefined;
      }

      // Configure Sentry with or without tracing
      const integrations = browserTracingIntegration
        ? [browserTracingIntegration]
        : [];

      window.Sentry.init({
        dsn: "https://2f3118ef2eb26366530690a7688315f4@o223951.ingest.us.sentry.io/4510148411326464",
        environment: region, // Use region as environment (us-east, eu-west, etc.)
        release: `${appVersion}-${region}`, // Release format: v1.2.3-us-east
        sampleRate: 0.1,

        // Sentry Tracing Configuration - only if BrowserTracing is available
        ...(browserTracingIntegration && {
          tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0, // Capture 20% of transactions in production, 100% in dev
        }),
        integrations: integrations,

        beforeSend: function (event: any) {
          // Narrowed 404 filter: only suppress chunk-loading 404s from stale
          // PWA cache (e.g. /app-v3/assets/utils-*.js after deployment).
          // Real API 404s are NOT suppressed — they indicate bugs.
          const isStaleChunk404 = event.exception?.values?.some((ex: any) => {
            const value = ex.value ?? "";
            if (!value.includes("Request failed with status code 404"))
              return false;
            const requestUrl =
              event.request?.url ?? event.extra?.url ?? "";
            const stackFrames = ex.stacktrace?.frames ?? [];
            const hasAssetFrame = stackFrames.some(
              (f: any) =>
                f.filename && /\/assets\/[\w-]+\.\w+\.(js|css)/.test(f.filename)
            );
            return (
              /\/assets\/[\w-]+\.\w+\.(js|css)/.test(requestUrl) || hasAssetFrame
            );
          });
          if (isStaleChunk404) {
            return null;
          }

          const ignorePatterns = [
            "Script error",
            "messageHandlers",
            "Failed to send the request",
            "runtime.sendMessage",
            "Tab not found",
            "WKWebView",
            "deallocated before the message",
            "Java object is gone",
            "Method not found",
            "postEvent",
            "navigationPerformanceLogger",
            "InvalidAccessError",
            "not a valid JavaScript MIME type",
            "Unable to preload CSS",
            "captured as exception with keys",
            "Object captured as exception with keys: [object has no keys]",
            "Error: undefined",
            "Request failed with status code 503",
          ];

          if (
            event.exception?.values?.some(
              (ex: any) =>
                ex.value &&
                ignorePatterns.some((pattern) => ex.value.includes(pattern))
            )
          ) {
            return null;
          }

          if (
            event.message &&
            ignorePatterns.some((pattern) => event.message.includes(pattern))
          ) {
            return null;
          }

          if (
            event.exception?.values?.some(
              (ex: any) =>
                ex.type &&
                ex.value &&
                ignorePatterns.some(
                  (pattern) =>
                    ex.type.includes(pattern) || ex.value.includes(pattern)
                )
            )
          ) {
            return null;
          }

          const routerIgnorePatterns = [
            "Redirected when going from",
            "Navigation cancelled from",
            "NavigationDuplicated",
            "Avoided redundant navigation",
          ];

          if (
            event.exception?.values?.some(
              (ex: any) =>
                ex.value &&
                routerIgnorePatterns.some((pattern) => ex.value.includes(pattern))
            )
          ) {
            return null;
          }

          event.tags = {
            component: "vue-app",
            framework: "vue-2.7",
            region: region,
            app_version: appVersion,
          };
          return event;
        },
      });

      console.log(
        `[Sentry] ✅ Successfully initialized for region: ${region}, release: ${appVersion}-${region}`
      );

      if (browserTracingIntegration) {
        console.log(
          "[Sentry] 🎯 Performance tracing ENABLED - monitoring routes and components"
        );
      } else {
        console.log(
          "[Sentry] ⚠️  Performance tracing DISABLED - using basic error tracking only"
        );
        console.log(
          "[Sentry] Manual breadcrumbs and navigation tracking still active"
        );
      }
    } else {
      console.error("[Sentry] ❌ window.Sentry is undefined after CDN load");
    }
  } catch (error) {
    console.error("[Sentry] ❌ Initialization failed:", error);
  }
}

function loadSentryCDNWithTracing(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if Sentry is already loaded (might have been loaded by another script)
    if (window.Sentry) {
      console.log("[Sentry] Already loaded by another source");
      resolve();
      return;
    }

    const script = document.createElement("script");
    // Use the tracing bundle instead of the basic bundle
    script.src = "https://browser.sentry-cdn.com/7.114.0/bundle.tracing.min.js";
    script.async = true;

    // Add timeout (10 seconds)
    const timeout = setTimeout(() => {
      console.error("[Sentry] CDN load timeout after 10 seconds");
      script.remove();
      reject(new Error("Sentry CDN load timeout"));
    }, 10000);

    script.onload = () => {
      clearTimeout(timeout);
      console.log("[Sentry] CDN script with tracing loaded successfully");
      // Give Sentry a moment to initialize
      setTimeout(() => {
        if (window.Sentry) {
          resolve();
        } else {
          reject(new Error("Sentry not available after script load"));
        }
      }, 100);
    };

    script.onerror = (event) => {
      clearTimeout(timeout);
      console.error("[Sentry] CDN script failed to load:", event);
      reject(new Error("Sentry CDN script load failed"));
    };

    console.log("[Sentry] Appending script to document head");
    document.head.appendChild(script);
  });
}

/**
 * Custom breadcrumb helper functions for tracking user actions
 */
export function addCustomBreadcrumb(
  category: string,
  message: string,
  data?: any
) {
  if (window.Sentry) {
    window.Sentry.addBreadcrumb({
      category: category,
      message: message,
      level: "info",
      data: data,
    });
  }
}

export function addComponentBreadcrumb(componentName: string, action: string) {
  addCustomBreadcrumb("ui.component", `Component ${action}: ${componentName}`, {
    component: componentName,
    action: action,
  });
}

export function addRouteBreadcrumb(from: string, to: string) {
  addCustomBreadcrumb("navigation", `Navigated from ${from} to ${to}`, {
    from: from,
    to: to,
  });
}

export function addUserActionBreadcrumb(action: string, details?: any) {
  addCustomBreadcrumb("user.action", `User performed: ${action}`, {
    action: action,
    ...details,
  });
}

/**
 * Detect deployment region from hostname for multi-region Sentry tracking
 */
function detectRegion(): string {
  if (typeof window === "undefined") return "unknown";

  const hostname = window.location.hostname.toLowerCase();

  // Map specific hostnames to regions
  if (hostname === "dev25.entry4sports.co.uk") return "dev";
  if (hostname === "eire.entry4sports.com") return "ireland";
  if (hostname === "entry.athleticsireland.ie") return "ireland-athletics";
  if (hostname === "entry4sports.co.uk") return "uk";
  if (hostname === "uat.entry4sports.co.uk") return "uat";
  if (hostname === "api.entry.athleticsireland.ie") return "ireland-api";

  // Fallback patterns for future hostnames
  if (hostname.includes("dev")) return "dev";
  if (hostname.includes("uat") || hostname.includes("staging")) return "uat";
  if (hostname.includes("ireland") || hostname.includes("athleticsireland"))
    return "ireland";
  if (hostname.includes("entry4sports")) return "uk";

  // Default to production if no specific region detected
  return "production";
}

/**
 * Get app version from global constant or fallback
 */
function getAppVersion(): string {
  return typeof __SENTRY_RELEASE__ !== "undefined" ? __SENTRY_RELEASE__ : "1.0.0";
}

export function captureError(error: Error, context?: any) {
  if (
    window.Sentry &&
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost"
  ) {
    if (context) {
      window.Sentry.setContext("custom", context);
    }
    window.Sentry.captureException(error);
  }
}

/**
 * How to Read Sentry Performance Data Guide
 *
 * 1. PERFORMANCE TAB: Shows all captured transactions (route loads, API calls)
 *    - Filter by route name to see specific page performance
 *    - Look at duration, breakdown, and user impact
 *
 * 2. TRACES: Shows the complete user journey through your application
 *    - See the sequence of routes users take
 *    - Identify common navigation patterns
 *    - Find where users drop off
 *
 * 3. BREADCRUMBS: Shows detailed user actions within each session
 *    - Component interactions (buttons clicked, forms submitted)
 *    - Navigation events (route changes)
 *    - Custom actions you've tracked
 *
 * 4. RELEASES: Compare performance between different app versions
 *    - See if new features impact performance
 *    - Track adoption of new routes
 *
 * 5. USER FEEDBACK: Correlate performance with user satisfaction
 *    - Slow transactions often correlate with frustrated users
 *    - Fast, smooth flows indicate good UX
 *
 * Key Metrics to Monitor:
 * - Transaction Duration: How long routes take to load
 * - Throughput: How many users are using each route
 * - Error Rates: Which routes/components have most errors
 * - User Flows: Common navigation paths through your app
 */
